/* 버전 관리 S3 저장소 — 레지스트리 버킷(app/·firmware/) 업로드·목록·삭제·latest(롤백) RMW.
   registryBucket.js(미러 클라이언트)만 사용. 스키마는 앱 파서와 lockstep 고정:
     app/manifest.json      = { version, notes, installer_url, sha256, released_at }   ← Windows
     app/manifest.mac.json  = 같은 5필드 스키마                                          ← macOS
     firmware/manifest.json = { firmware: [ { type, version, url, sha256, notes?, released_at? } ] }

   ★ macOS 를 왜 별도 manifest 로 두나 (BE-0009)
     app/manifest.json 은 최상위가 단일 릴리즈(version/installer_url/sha256)라 두 플랫폼을
     담을 자리가 없다. 여기에 platform 키를 끼워 넣으면 이미 출하된 Windows 앱의
     parseAppManifest 가 엉뚱한 version 을 읽거나 FormatException 을 던진다(코드 예제
     manifest 분리와 같은 이유). 그래서 스키마는 **그대로 복제**하고 파일만 나눈다 —
     mac 앱은 APP_UPDATE_MANIFEST_URL 만 app/manifest.mac.json 으로 바꾸면 기존
     parseAppManifest 를 한 줄도 안 고치고 그대로 쓴다. 구버전 Windows 앱은 이 파일의
     존재 자체를 모르므로 회귀 0.
   ★ url 은 모두 **버킷상대**(app/<name>, firmware/<type>/<ver>.bin) 로 기록한다 —
     · 앱 펌웨어 파서(lib/registry/s3_firmware_registry.dart _resolveBinary)는 binaryBaseUrl 에
       entry.url 을 prepend 하므로 절대 URL 이면 다운로드가 깨진다(상대 필수).
     · 앱 업데이트 파서(lib/update/s3_app_update_service.dart _resolveInstallerUrl)는 상대/절대
       모두 허용하지만 CI 산출물·정본 docstring 이 상대형이라 통일한다.
     keyFromUrl 은 상대형도 그대로 키로 환원하므로 목록 latest 매칭에 무영향.
   목록은 실제 ListObjectsV2 결과 기준(키 패턴 가정 최소화). manifest 가 가리키는 현재 latest 만
   별도 표시·삭제 가드한다. 업로드(.exe/.bin)는 sha256 계산 → 바이너리+.sha256 사이드카 PUT →
   manifest 기록(앱=덮어쓰기, 펌웨어=type entry RMW). */
import {
  getJsonAnon, getTextAnon, putObject, deleteObject, listObjects, seedFakeObjects,
} from '../../api/registryBucket.js';
import { REGISTRY_BUCKET_URL } from '../../config/env.js';

/* sha256 hex — Web Crypto. buffer = ArrayBuffer (설치파일/펌웨어 무결성). */
async function sha256Hex(buffer) {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export const APP_PREFIX = 'app/';
export const MAC_PREFIX = 'app/mac/';                  // macOS 설치본은 app/ 하위 폴더로 격리
export const FIRMWARE_PREFIX = 'firmware/';
export const APP_MANIFEST_KEY = 'app/manifest.json';
export const MAC_MANIFEST_KEY = 'app/manifest.mac.json';
export const FIRMWARE_MANIFEST_KEY = 'firmware/manifest.json';

/* ★ 패널에 노출할 "아티팩트"만 판정(2단계 릴리스 안전장치).
   설치파일/펌웨어 바이너리만 행으로 그리고, manifest.json·staging.json(과거 잔재)·*.sha256·기타
   제어/메타 객체는 전부 숨긴다. .json 을 "latest 로" 눌러 manifest 가 .json 을 가리키는 사고를 원천 차단. */
const APP_ARTIFACT_RE = /^Huenit_Lab_.+_Setup\.exe$/i;     // app/Huenit_Lab_<ver>_Setup.exe
const MAC_ARTIFACT_RE = /\.(dmg|pkg)$/i;                   // app/mac/<name>.dmg | .pkg
const isFwArtifact = (key) => /\.bin$/i.test(key || '');   // firmware/<type>/<ver>.bin (.sha256 제외)

/* 앱 설치파일의 플랫폼 — app/mac/ 하위면 mac, 그 외 app/ 직하는 windows.
   (mac 채널이 생기기 전 업로드분은 전부 app/ 직하라 windows 로 남는다.) */
export const appPlatform = (key) => (String(key || '').startsWith(MAC_PREFIX) ? 'mac' : 'windows');
const isWinArtifact = (key) => appPlatform(key) === 'windows'
  && APP_ARTIFACT_RE.test((key || '').split('/').pop() || '');
const isMacArtifact = (key) => appPlatform(key) === 'mac' && MAC_ARTIFACT_RE.test(key || '');
const isAppArtifact = (key) => isWinArtifact(key) || isMacArtifact(key);

/* 설치파일 이름에서 버전 파싱: Windows Setup / macOS Apple Silicon 산출물. */
export function parseAppVersion(name) {
  const fileName = String(name || '').split('/').pop();
  const windows = fileName.match(/^Huenit_Lab_(.+)_Setup\.exe$/i);
  if (windows) return windows[1];
  const mac = fileName.match(/^Huenit_Lab_(.+?)(?:_macos_arm64)?\.(dmg|pkg)$/i);
  return mac ? mac[1] : '';
}

/* 플랫폼별 발행 스펙 — 단일 정본. 새 플랫폼이 생기면 여기만 늘린다. */
export const APP_PLATFORMS = {
  windows: {
    label: 'Windows', prefix: APP_PREFIX, manifestKey: APP_MANIFEST_KEY,
    accept: '.exe', isArtifact: isWinArtifact,
    hint: '설치파일(.exe)을 업로드하세요.',
    contentType: () => 'application/vnd.microsoft.portable-executable',
  },
  mac: {
    label: 'macOS', prefix: MAC_PREFIX, manifestKey: MAC_MANIFEST_KEY,
    accept: '.dmg,.pkg', isArtifact: isMacArtifact,
    hint: '설치파일(.dmg 또는 .pkg)을 업로드하세요.',
    contentType: (name) => (/\.pkg$/i.test(name) ? 'application/vnd.apple.installer+xml' : 'application/x-apple-diskimage'),
  },
};
const platformSpec = (p) => APP_PLATFORMS[p] || APP_PLATFORMS.windows;

const urlFor = (key) => (REGISTRY_BUCKET_URL || '') + '/' + key;
/* 객체 공개 URL (다운로드용) — registry 버킷은 public-read. */
export const objectUrl = (key) => urlFor(key);
function keyFromUrl(url) {
  if (!url) return '';
  if (REGISTRY_BUCKET_URL && url.startsWith(REGISTRY_BUCKET_URL)) return url.slice(REGISTRY_BUCKET_URL.length).replace(/^\//, '');
  try { return new URL(url).pathname.replace(/^\//, ''); } catch (e) { return String(url).replace(/^\//, ''); }
}

const MANIFEST_KEYS = new Set([APP_MANIFEST_KEY, MAC_MANIFEST_KEY, FIRMWARE_MANIFEST_KEY]);
export const isManifestKey = (key) => MANIFEST_KEYS.has(key);
export const isProtectedKey = (key) => isManifestKey(key) || (key || '').endsWith('/');

/* S3 에러 정규화 — IAM 미적용(AccessDenied/403)·세션만료(401) 를 status 로 통일(배너 분기). */
function normalizeS3Error(e) {
  const msg = (e && e.message) || '';
  const code = (e && e.$metadata && e.$metadata.httpStatusCode) || (e && e.status);
  const name = e && e.name;
  if (code === 401 || name === 'NotAuthorized' || name === 'ExpiredToken' || /\b401\b|expired|unauthor/i.test(msg)) {
    const err = new Error('세션이 만료되었습니다 — 다시 로그인하세요.'); err.status = 401; return err;
  }
  if (code === 403 || name === 'AccessDenied' || /access ?denied|forbidden|\b403\b/i.test(msg)) {
    const err = new Error('권한이 필요합니다 — plan.md §15 IAM 정책을 Identity Pool developer 역할에 적용하세요.');
    err.status = 403; return err;
  }
  return e instanceof Error ? e : new Error(String(e));
}

/* manifest 읽기 — 없음(404)·익명 GET 권한없음(403) → null(빈 상태 안내, throw 안 함).
   manifest 는 public-read 라 IAM 미적용이어도 보통 읽힌다(목록만 403). */
async function readManifest(key) {
  try {
    return await getJsonAnon(urlFor(key));
  } catch (e) {
    if (e && (e.status === 404 || e.status === 403)) return null;
    throw normalizeS3Error(e);
  }
}

/* 앱 패널 — app/ 객체 목록(Windows + macOS 한 번에) + 플랫폼별 latest.
   ListObjectsV2 는 delimiter 를 안 쓰므로 app/mac/* 도 같은 호출에 포함된다 → 요청 1회.
   행마다 platform('windows'|'mac')을 달아 두고 화면에서 좌우로 나눠 쓴다(펌웨어 type 과 동일).
   referenced = 각 플랫폼 latest 설치파일 + 그 .sha256 사이드카(삭제 가드 대상). */
export async function listApp(getCredentials) {
  let objects;
  try { objects = await listObjects(getCredentials, APP_PREFIX); }
  catch (e) { throw normalizeS3Error(e); }
  const [manifest, macManifest] = await Promise.all([
    readManifest(APP_MANIFEST_KEY), readManifest(MAC_MANIFEST_KEY),
  ]);
  const keyOf = (m) => (m && m.installer_url ? keyFromUrl(m.installer_url) : null);
  const latestKey = keyOf(manifest);
  const macLatestKey = keyOf(macManifest);

  const referenced = new Set();
  const latestKeys = new Set();
  for (const k of [latestKey, macLatestKey]) {
    if (!k) continue;
    latestKeys.add(k);
    referenced.add(k); referenced.add(k + '.sha256');
  }
  const entry = (k, m) => (k ? { key: k, version: m.version, sha256: m.sha256 } : null);
  return {
    kind: 'app',
    manifest, macManifest,
    latest: entry(latestKey, manifest),
    macLatest: entry(macLatestKey, macManifest),
    referenced,
    rows: buildRows(objects, referenced, latestKeys, isAppArtifact),
    totalSize: objects.reduce((n, o) => n + (o.size || 0), 0),
  };
}

/* 펌웨어 패널 — firmware/ 객체 목록 + type 별 latest(manifest.firmware[].url 매칭 키). */
export async function listFirmware(getCredentials) {
  let objects;
  try { objects = await listObjects(getCredentials, FIRMWARE_PREFIX); }
  catch (e) { throw normalizeS3Error(e); }
  const manifest = await readManifest(FIRMWARE_MANIFEST_KEY);
  const list = manifest && Array.isArray(manifest.firmware) ? manifest.firmware : [];
  const latestKeys = new Set();          // ★ 뱃지(실제 바이너리)
  const referenced = new Set();          // 삭제 가드(바이너리 + 사이드카)
  const latestByType = {};
  for (const f of list) {
    if (!f || !f.url) continue;
    const k = keyFromUrl(f.url);
    latestKeys.add(k); referenced.add(k); referenced.add(k + '.sha256');
    latestByType[f.type] = { key: k, version: f.version, sha256: f.sha256 };
  }
  return {
    kind: 'firmware',
    manifest,
    latestByType,
    referenced,
    rows: buildRows(objects, referenced, latestKeys, isFwArtifact, /*firmware*/ true),
    totalSize: objects.reduce((n, o) => n + (o.size || 0), 0),
  };
}

/* 객체 목록 → 표시 행. ★ isArtifact 통과 객체(설치파일/펌웨어 바이너리)만 행으로.
   manifest.json·staging.json·*.sha256·기타 메타는 숨김. .sha256 사이드카는 부모 .exe/.bin 행에
   흡수(sha256 ✓ 표시·롤백 자동채움용)되며 자기 자신이 행이 되지는 않는다. */
function buildRows(objects, referenced, latestKeys, isArtifact, isFirmware = false) {
  const prefix = isFirmware ? FIRMWARE_PREFIX : APP_PREFIX;
  const sidecars = new Map();
  objects.forEach(o => { if (o.key.endsWith('.sha256')) sidecars.set(o.key, o); });

  const rows = objects
    .filter(o => !o.key.endsWith('/') && isArtifact(o.key))
    .map(o => ({
      key: o.key,
      size: o.size,
      lastModified: o.lastModified,
      name: o.key.slice(prefix.length),
      type: isFirmware ? firmwareType(o.key) : null,
      platform: isFirmware ? null : appPlatform(o.key),
      sidecar: sidecars.get(o.key + '.sha256') || null,
      isLatest: latestKeys.has(o.key),
      isReferenced: referenced.has(o.key),
      canSetLatest: true,
    }));
  rows.sort((a, b) => String(b.lastModified || '').localeCompare(String(a.lastModified || '')));
  return rows;
}

// firmware/{type}/{version}.bin → type. 관례일 뿐 — prefix 한 단계만 추출(없으면 null).
function firmwareType(key) {
  const rest = key.slice(FIRMWARE_PREFIX.length);
  const i = rest.indexOf('/');
  return i > 0 ? rest.slice(0, i) : null;
}

/* installer/binary 의 .sha256 사이드카 GET(없으면 null) — 롤백 시 무결성 prefill. */
export async function fetchSidecarSha256(key) {
  const txt = await getTextAnon(urlFor(key + '.sha256'));
  if (!txt) return null;
  const m = String(txt).trim().match(/[a-fA-F0-9]{64}/);   // "<hex>" 또는 "<hex>  <name>"
  return m ? m[0].toLowerCase() : null;
}

/* 앱 latest 교체(롤백) — 대상 키의 플랫폼에 해당하는 manifest 만 통째 PUT.
   플랫폼은 키에서 도출한다 — 화면이 잘못 넘겨도 mac 설치본이 Windows manifest 에
   기록되는 사고가 나지 않게(교차 오염 차단). */
export async function setAppLatest(getCredentials, { key, version, notes, sha256 }) {
  if (isProtectedKey(key)) throw new Error('manifest/디렉토리는 latest 로 지정할 수 없습니다.');
  if (!isAppArtifact(key)) throw new Error('설치파일(Windows .exe · macOS .dmg/.pkg)만 latest 로 지정할 수 있습니다.');
  const spec = platformSpec(appPlatform(key));
  const sha = String(sha256 || '').trim().toLowerCase();
  if (!/^[a-f0-9]{64}$/.test(sha)) throw new Error('sha256 은 64자리 16진수여야 합니다 (.sha256 사이드카에서 읽어 채웁니다).');
  // 정확히 5필드만(앱 jsonDecode 계약). 부분수정/머지 없음 — 객체 통째 단일 PUT. JSON.stringify=BOM 없음('{' 로 시작).
  const manifest = {
    version: String(version || '').trim(),
    notes: typeof notes === 'string' ? notes : '',
    installer_url: key,                                  // app/Huenit_Lab_<ver>_Setup.exe
    sha256: sha,                                         // .sha256 사이드카 값 그대로(재계산 안 함)
    released_at: new Date().toISOString(),               // 승격(현재) 시각, ISO8601 Z
  };
  try {
    await putObject(getCredentials, spec.manifestKey, JSON.stringify(manifest, null, 2), 'application/json', { cacheControl: 'max-age=300' });
  } catch (e) { throw normalizeS3Error(e); }
  return manifest;
}

/* 펌웨어 latest 교체(롤백) — firmware/manifest.json 의 해당 type entry 교체 RMW. */
export async function setFirmwareLatest(getCredentials, { key, type, version, notes, sha256, releasedAt }) {
  if (isProtectedKey(key)) throw new Error('manifest/디렉토리는 latest 로 지정할 수 없습니다.');
  if (!isFwArtifact(key)) throw new Error('펌웨어 바이너리(.bin)만 latest 로 지정할 수 있습니다.');
  if (!type) throw new Error('펌웨어 type(robot/camera)을 확인할 수 없습니다.');
  const manifest = (await readManifest(FIRMWARE_MANIFEST_KEY)) || {};
  const list = Array.isArray(manifest.firmware) ? manifest.firmware.slice() : [];
  const entry = {
    type,
    version: (version || '').trim(),
    url: key,                                            // 버킷상대 (firmware/<type>/<ver>.bin) — 앱 _resolveBinary 가 base 에 prepend
    sha256: (sha256 || '').trim(),
    ...(notes && notes.trim() ? { notes: notes.trim() } : {}),
    released_at: releasedAt || new Date().toISOString(),
  };
  const idx = list.findIndex(f => f && f.type === type);
  if (idx >= 0) list[idx] = entry; else list.push(entry);
  const next = { ...manifest, firmware: list };
  try { await putObject(getCredentials, FIRMWARE_MANIFEST_KEY, JSON.stringify(next, null, 2), 'application/json', { cacheControl: 'max-age=300' }); }
  catch (e) { throw normalizeS3Error(e); }
  return next;
}

/* 앱 새 버전 업로드 — 설치파일 PUT(+ .sha256 사이드카) → 해당 플랫폼 manifest 덮어쓰기(=새 latest).
   CI 자동 업로드와 같은 산출물의 수동 경로. fileBuffer = ArrayBuffer, fileName = 원본 파일명.
   platform: 'windows'(app/<name>, manifest.json) | 'mac'(app/mac/<name>, manifest.mac.json).
   installer_url 은 두 플랫폼 모두 버킷상대. onStep(label) = 대용량 PUT 진행 표시 콜백. */
export async function uploadAppVersion(getCredentials, { platform = 'windows', fileName, fileBuffer, version, notes, onStep }) {
  const step = (s) => { if (onStep) onStep(s); };
  const spec = platformSpec(platform);
  const name = (fileName || '').trim();
  const key = spec.prefix + name;                        // app/<name> · app/mac/<name>
  if (!spec.isArtifact(key)) throw new Error(spec.hint);
  if (!fileBuffer || !fileBuffer.byteLength) throw new Error('빈 파일입니다.');
  if (!(version || '').trim()) throw new Error('버전을 입력하세요.');

  step('체크섬 계산 중…');
  const sha256 = await sha256Hex(fileBuffer);
  step('설치파일 업로드 중…');
  try {
    await putObject(getCredentials, key, new Uint8Array(fileBuffer), spec.contentType(name));
    await putObject(getCredentials, key + '.sha256', `${sha256}  ${name}`, 'text/plain');
  } catch (e) { throw normalizeS3Error(e); }

  step('manifest 갱신 중…');
  const manifest = {
    version: (version || '').trim(),
    notes: (notes || '').trim(),
    installer_url: key,                                  // 버킷상대 (앱 _resolveInstallerUrl 이 origin 에 join)
    sha256,
    released_at: new Date().toISOString(),
  };
  try { await putObject(getCredentials, spec.manifestKey, JSON.stringify(manifest, null, 2), 'application/json'); }
  catch (e) { throw normalizeS3Error(e); }
  return manifest;
}

/* 펌웨어 새 버전 업로드 — .bin PUT(+ .sha256) → firmware/manifest.json 의 해당 type entry RMW.
   url 은 버킷상대(firmware/<type>/<ver>.bin) — 앱 _resolveBinary 가 binaryBaseUrl 에 prepend 하므로 필수. */
export async function uploadFirmware(getCredentials, { type, version, notes, fileBuffer, onStep }) {
  const step = (s) => { if (onStep) onStep(s); };
  const t = (type || '').trim();
  const v = (version || '').trim();
  if (t !== 'robot' && t !== 'camera') throw new Error('펌웨어 type 은 robot 또는 camera 여야 합니다.');
  if (!v) throw new Error('버전을 입력하세요.');
  if (!fileBuffer || !fileBuffer.byteLength) throw new Error('빈 파일입니다.');
  const key = `${FIRMWARE_PREFIX}${t}/${v}.bin`;         // firmware/<type>/<ver>.bin

  step('체크섬 계산 중…');
  const sha256 = await sha256Hex(fileBuffer);
  step('펌웨어 업로드 중…');
  try {
    await putObject(getCredentials, key, new Uint8Array(fileBuffer), 'application/octet-stream');
    await putObject(getCredentials, key + '.sha256', `${sha256}  ${v}.bin`, 'text/plain');
  } catch (e) { throw normalizeS3Error(e); }

  step('manifest 갱신 중…');
  const manifest = (await readManifest(FIRMWARE_MANIFEST_KEY)) || {};
  const list = Array.isArray(manifest.firmware) ? manifest.firmware.slice() : [];
  const entry = {
    type: t,
    version: v,
    url: key,                                            // 버킷상대
    sha256,
    ...(notes && notes.trim() ? { notes: notes.trim() } : {}),
    released_at: new Date().toISOString(),
  };
  const idx = list.findIndex(f => f && f.type === t);
  if (idx >= 0) list[idx] = entry; else list.push(entry);
  const next = { ...manifest, firmware: list };
  try { await putObject(getCredentials, FIRMWARE_MANIFEST_KEY, JSON.stringify(next, null, 2), 'application/json'); }
  catch (e) { throw normalizeS3Error(e); }
  return next;
}

/* 객체 삭제 — manifest/디렉토리는 차단(방어). 참조중(latest) 가드는 UI(버튼 비활성)에서. */
export async function deleteRegistryObject(getCredentials, key) {
  if (isProtectedKey(key)) throw new Error('manifest 또는 보호된 키는 삭제할 수 없습니다.');
  try { await deleteObject(getCredentials, key); }
  catch (e) { throw normalizeS3Error(e); }
}

/* ---------- fake 모드 데모 시드 (env 미설정 시 UI 개발/시연) ---------- */
/* 시드 버전 — 데모 데이터가 바뀌면 올린다(기존 fake 사용자도 새 시드를 받게).
   v2: macOS 채널 추가 + Windows 파일명을 실제 산출물 규칙(Huenit_Lab_<ver>_Setup.exe)으로
       정정. 이전 이름(HuenitLab-…-setup.exe)은 APP_ARTIFACT_RE 에 걸리지 않아 데모에서
       앱 행이 아예 안 보였다. */
const SEED_FLAG = 'adm.fake.registrySeeded.v2';
const HEX = (c) => c.repeat(64);   // 데모용 64-hex sha256

export function seedFake() {
  try { if (localStorage.getItem(SEED_FLAG)) return; } catch (e) { return; }

  const app7 = 'Huenit_Lab_0.6.0-beta.7_Setup.exe';
  const app6 = 'Huenit_Lab_0.6.0-beta.6_Setup.exe';
  const mac7 = 'Huenit_Lab_0.6.0-beta.7.dmg';
  const mac6 = 'Huenit_Lab_0.6.0-beta.6.dmg';
  const sha7 = HEX('a'), sha6 = HEX('b');
  const shaM7 = HEX('f'), shaM6 = HEX('9');
  const robot14 = 'firmware/robot/1.4.0.bin', robot13 = 'firmware/robot/1.3.0.bin';
  const cam21 = 'firmware/camera/2.1.0.bin';
  const shaR14 = HEX('c'), shaR13 = HEX('d'), shaC21 = HEX('e');

  const appManifest = {
    version: '0.6.0-beta.7',
    notes: '코드 탭 개선 · 오프라인 캐시',
    installer_url: 'app/' + app7,                        // 버킷상대 (실 업로드 산출물과 동일 형식)
    sha256: sha7,
    released_at: '2026-06-08T02:00:00Z',
  };
  const macManifest = {
    version: '0.6.0-beta.7',
    notes: 'macOS 첫 배포본',
    installer_url: MAC_PREFIX + mac7,                    // app/mac/<name>.dmg
    sha256: shaM7,
    released_at: '2026-06-08T02:10:00Z',
  };
  const fwManifest = {
    firmware: [
      { type: 'robot', version: '1.4.0', url: robot14, sha256: shaR14, notes: 'arm limit 보정', released_at: '2026-06-01T00:00:00Z' },
      { type: 'camera', version: '2.1.0', url: cam21, sha256: shaC21, released_at: '2026-05-28T00:00:00Z' },
    ],
  };

  seedFakeObjects([
    { key: 'app/' + app7, body: '[installer]', size: 84_213_760, lastModified: '2026-06-08T02:00:00Z' },
    { key: 'app/' + app7 + '.sha256', body: `${sha7}  ${app7}`, contentType: 'text/plain', lastModified: '2026-06-08T02:00:00Z' },
    { key: 'app/' + app6, body: '[installer]', size: 83_770_112, lastModified: '2026-05-20T05:30:00Z' },
    { key: 'app/' + app6 + '.sha256', body: `${sha6}  ${app6}`, contentType: 'text/plain', lastModified: '2026-05-20T05:30:00Z' },
    { key: APP_MANIFEST_KEY, body: JSON.stringify(appManifest, null, 2), contentType: 'application/json', lastModified: '2026-06-08T02:00:00Z' },

    { key: MAC_PREFIX + mac7, body: '[installer]', size: 96_468_992, lastModified: '2026-06-08T02:10:00Z' },
    { key: MAC_PREFIX + mac7 + '.sha256', body: `${shaM7}  ${mac7}`, contentType: 'text/plain', lastModified: '2026-06-08T02:10:00Z' },
    { key: MAC_PREFIX + mac6, body: '[installer]', size: 95_944_704, lastModified: '2026-05-20T05:40:00Z' },
    { key: MAC_PREFIX + mac6 + '.sha256', body: `${shaM6}  ${mac6}`, contentType: 'text/plain', lastModified: '2026-05-20T05:40:00Z' },
    { key: MAC_MANIFEST_KEY, body: JSON.stringify(macManifest, null, 2), contentType: 'application/json', lastModified: '2026-06-08T02:10:00Z' },

    { key: robot14, body: '[firmware]', size: 524_288, lastModified: '2026-06-01T00:00:00Z' },
    { key: robot14 + '.sha256', body: `${shaR14}  1.4.0.bin`, contentType: 'text/plain', lastModified: '2026-06-01T00:00:00Z' },
    { key: robot13, body: '[firmware]', size: 520_192, lastModified: '2026-04-15T00:00:00Z' },
    { key: robot13 + '.sha256', body: `${shaR13}  1.3.0.bin`, contentType: 'text/plain', lastModified: '2026-04-15T00:00:00Z' },
    { key: cam21, body: '[firmware]', size: 1_048_576, lastModified: '2026-05-28T00:00:00Z' },
    { key: cam21 + '.sha256', body: `${shaC21}  2.1.0.bin`, contentType: 'text/plain', lastModified: '2026-05-28T00:00:00Z' },
    { key: FIRMWARE_MANIFEST_KEY, body: JSON.stringify(fwManifest, null, 2), contentType: 'application/json', lastModified: '2026-06-01T00:00:00Z' },
  ]);

  try { localStorage.setItem(SEED_FLAG, '1'); } catch (e) { /* ignore */ }
}
